import React from "react";
import { Helmet } from "react-helmet";
import {
  academicProfile,
  certifications,
  contactForm,
  contactPageData,
  greeting,
  seo,
  socialMediaLinks,
} from "../../portfolio.js";

const email = (contactForm.bookChatMailto || "")
  .replace(/^mailto:/, "")
  .split("?")[0];

/** schema.org wants absolute URLs; the headshot is served from this origin. */
const absolute = (path) =>
  /^https?:/.test(path) ? path : new URL(path, seo.og.url).href;

/** schema.org Person. Built once at module load; it never varies per page. */
const personLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: greeting.title,
  url: seo.og.url,
  image: absolute(academicProfile.headshotUrl),
  email: email || undefined,
  jobTitle: academicProfile.role,
  description: seo.description,
  knowsAbout: academicProfile.researchInterests,
  sameAs: socialMediaLinks
    .filter((m) => /^https?:/.test(m.link))
    .map((m) => m.link),
  affiliation: {
    "@type": "CollegeOrUniversity",
    name: academicProfile.institution,
    url: academicProfile.institutionUrl,
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: contactPageData.addressSection.locality,
    addressRegion: contactPageData.addressSection.region,
    addressCountry: contactPageData.addressSection.country,
  },
  hasCredential: certifications.certifications.map((c) => ({
    "@type": "EducationalOccupationalCredential",
    name: c.title,
    description: c.subtitle,
    url: c.certificate_link || undefined,
  })),
};

export default function Seo({ title, description }) {
  const pageTitle = title ? `${title} | ${greeting.title}` : seo.title;
  const pageDesc = description || seo.description;

  return (
    <Helmet>
      <html lang="en" />
      <body className="signal-body" />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="author" content={greeting.title} />
      {/* theme-color and color-scheme are deliberately not declared here.
          Helmet would reassert them on every route change and stomp the value
          ThemeToggle sets; index.html owns them instead. */}

      <meta property="og:site_name" content={greeting.title} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content={seo.og.type} />
      <meta property="og:url" content={seo.og.url} />
      {/* Dimensions let a scraper reserve the right space before the image
          lands, which is what stops previews rendering as a thin strip. */}
      <meta property="og:image" content={seo.og.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content={`${greeting.title} · ${academicProfile.role}`}
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={seo.og.image} />
      <meta
        name="twitter:image:alt"
        content={`${greeting.title} · ${academicProfile.role}`}
      />

      <script type="application/ld+json">{JSON.stringify(personLd)}</script>
    </Helmet>
  );
}
